import { CHAT_LIMITS } from '@loop3/universe'
import { solutionPath } from '@/lib/routes'
import type { ChatTurn, Entity } from '@/lib/types/universe'

export { isUuid } from '@loop3/universe'

/** Trims, collapses whitespace and lowercases a search query. */
export function normalizeQuery(q: string): string {
  return q.trim().replace(/\s+/g, ' ').toLowerCase()
}

/** 100 exact, 80 prefix, 60 word-prefix, 40 contains, 0 none (case-insensitive). */
export function scoreMatch(query: string, text: string): number {
  const q = normalizeQuery(query)
  const t = normalizeQuery(text)
  if (!q || !t) return 0
  if (t === q) return 100
  if (t.startsWith(q)) return 80
  if (t.split(' ').some((w) => w.startsWith(q))) return 60
  return t.includes(q) ? 40 : 0
}

/** Orders by best title/subtitle score, then title. Never drops: the server already matched on fields cards don't show (e.g. email). */
export function rankEntities(query: string, entities: Entity[]): Entity[] {
  if (!normalizeQuery(query)) return entities
  return entities
    .map((e) => ({
      e,
      score: Math.max(
        scoreMatch(query, e.title),
        scoreMatch(query, e.subtitle ?? '')
      ),
    }))
    .sort((a, b) => b.score - a.score || a.e.title.localeCompare(b.e.title))
    .map((x) => x.e)
}

/** Where an entity card navigates: external href, an in-app route, or an Explorer search. */
export function entityLink(e: Entity): { to: string } | { href: string } {
  if (e.kind === 'solution' && e.id) return { to: solutionPath(e.id) }
  if (e.href) return { href: e.href }
  if (e.id) {
    if (e.kind === 'client') return { to: `/clients/${e.id}` }
    if (e.kind === 'company') return { to: `/explorer/companies/${e.id}` }
    if (e.kind === 'person' || e.kind === 'team_member')
      return { to: `/explorer/people/${e.id}` }
  }
  return { to: `/explorer?q=${encodeURIComponent(e.title)}` }
}

const MAX_HISTORY_TURNS = 10
// Safety headroom under Lupe's body cap.
const BODY_MARGIN_BYTES = 4096

const answered = (t: ChatTurn | undefined) =>
  t?.role === 'assistant' && !t.error && t.text.length > 0

const bodyBytes = (message: string, history: unknown) =>
  new TextEncoder().encode(JSON.stringify({ message, history })).length

/**
 * The last 10 turns of answered exchanges, as the plain history Lupe's chat endpoint expects.
 * Failed, empty or unanswered exchanges are left out, and the oldest turns are dropped until
 * `{ message, history }` fits Lupe's body cap.
 */
export function historyFor(
  turns: ChatTurn[],
  message = ''
): { role: 'user' | 'assistant'; text: string }[] {
  const history = turns
    .filter((t, i) =>
      t.role === 'user' ? answered(turns[i + 1]) : answered(t)
    )
    .slice(-MAX_HISTORY_TURNS)
    .map(({ role, text }) => ({
      role,
      text: text.slice(0, CHAT_LIMITS.historyText),
    }))
  const budget = CHAT_LIMITS.bodyBytes - BODY_MARGIN_BYTES
  while (
    history.length &&
    (history[0].role === 'assistant' || bodyBytes(message, history) > budget)
  )
    history.shift()
  return history
}

/** Drops person entities that duplicate a shown team member (same person id); the team card wins. */
export function dedupeTeamPersons(entities: Entity[]): Entity[] {
  const team = new Set(
    entities.flatMap((e) => (e.kind === 'team_member' && e.id ? [e.id] : []))
  )
  return entities.filter(
    (e) => !(e.kind === 'person' && e.id && team.has(e.id))
  )
}
